import React, { useState } from "react";
import { BarChart3 } from "lucide-react";
import { ScoreBar, Segmented } from "@/components/vb";
import ProgressTracker from "@/components/projects/blocks/ProgressTracker";
import { MarcusSummary } from "@/components/projects/blocks/MarcusSummary";

type Props = {
    report: any;
    /** Signed-in user is on the free plan (blurs the trend + Marcus summary). */
    isFree?: boolean;
    /** Company id — the trend tab fetches its own history from it. */
    companyId?: number;
    token?: string | null;
    minStartDate?: string;
    maxReportDate?: string;
    /** Open the billing tab (free-plan unlock). */
    onUnlock?: () => void;
};

const clamp = (n: number) => Math.max(0, Math.min(100, n));

/** The report's AI Trust Signal Score card — the left half of the dashboard hero.
 *  Replaces the radial gauge (AITS-491). A segmented tier ScoreBar (Invisible /
 *  Unverified / Trusted / Authority) with the period delta and the folded Marcus
 *  summary, plus a "Trend" tab (mirroring the VB verdict card's tab switch) that
 *  swaps in the progress line. Free/teaser reports gate the exact score behind an
 *  estimated range, matching the old gauge's teaser. */
export function ScoreCard({
    report,
    isFree = false,
    companyId,
    token,
    minStartDate,
    maxReportDate,
    onUnlock,
}: Props) {
    const [tab, setTab] = useState<"score" | "trend">("score");

    const totalScore = Math.round(clamp(Number(report?.analytics?.totalScore) || 0));
    const lastMonth =
        report?.last_month_score != null && report.last_month_score !== ""
            ? Math.round(clamp(parseFloat(report.last_month_score) * 100))
            : null;
    const delta = lastMonth != null ? totalScore - lastMonth : null;

    // Free/teaser report (≤9 signals): the exact score is withheld; show the
    // estimated range instead, like the gauge's free mode.
    const signalsCount = report?.signals ? Object.values(report.signals).flat().length : 0;
    const isFreeReport = !report?.is_paid && signalsCount <= 9;
    const speedometer = report?.speedometer;
    const rangeLow = Math.round(
        speedometer?.range_min != null ? clamp(Number(speedometer.range_min)) : clamp(totalScore - 15),
    );
    const rangeHigh = Math.round(
        speedometer?.range_max != null ? clamp(Number(speedometer.range_max)) : clamp(totalScore + 15),
    );

    return (
        <div className="flex h-full flex-col rounded-2xl border border-vb-line bg-vb-surface p-5 shadow-vb-sm sm:p-6">
            {/* Card title (the persistent eyebrow) on the left, the Score / Trend
                view toggle vertically aligned with it on the right — mirroring the
                VB verdict card's Verdict / vs-Competitors segmented control. */}
            <div className="mb-4 flex items-center justify-between gap-3">
                <div className="text-[11px] font-extrabold uppercase tracking-[0.14em] text-vb-text-3">
                    AI Trust Signal Score
                </div>
                <Segmented
                    ariaLabel="AI Trust Signal score view"
                    size="sm"
                    value={tab}
                    onChange={setTab}
                    options={[
                        { value: "score", label: "Score" },
                        { value: "trend", label: "Trend", icon: <BarChart3 size={12} /> },
                    ]}
                />
            </div>

            <div className="flex flex-1 flex-col">
                {tab === "score" ? (
                    <div className="flex flex-1 flex-col gap-4">
                        {isFreeReport ? (
                            <ScoreBar
                                score={(rangeLow + rangeHigh) / 2}
                                showNumber={false}
                                headline={`Estimated score range ${rangeLow}–${rangeHigh}. Subscribe to see your exact score.`}
                            />
                        ) : (
                            <ScoreBar score={totalScore} delta={delta} />
                        )}
                        <MarcusSummary report={report} isFreeUser={isFree} onUnlock={onUnlock} />
                    </div>
                ) : (
                    <ProgressTracker
                        trending={report?.trending}
                        isFree={isFree}
                        companyId={companyId}
                        token={token}
                        minStartDate={minStartDate}
                        maxReportDate={maxReportDate}
                        onUnlock={onUnlock}
                    />
                )}
            </div>
        </div>
    );
}

export default ScoreCard;
